import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { wormColors } from "core/src/core/constants.js";

import RemoteGame, { OnlineSession } from "../play/game/RemoteGame";
import Modal from "../../shared/modal/Modal";
import { subscribeLobbyCounts, LobbyCounts } from "../../online/net";
import { useAuth } from "../../shared/auth";
import { isAdmin } from "../../shared/admin";
import { makeGame, descToMapDef, fmtClock, SIZE_BY_SLUG, isStressSize } from "./lobby";
import { arenaLabel } from "../setup/maps";
import styles from "./LobbyPage.module.scss";

// A public matchmaking box's own page: /game/duel | /game/brawl | /game/royale.
// The lobby's cards navigate here; the session (waiting room + the match itself)
// lives entirely on this page, so the lobby is never mounted during online play,
// and the match has a real URL - reloading the browser reconnects into the same
// session (the room instance sticks per tab in net.ts, and the server hands a
// reloading client its seat back mid-match).
export default function OnlineMatchPage() {
  const params = useParams();
  const navigate = useNavigate();
  const size = SIZE_BY_SLUG[(params.mode || "").toLowerCase()];
  const [g] = useState(() => (size ? makeGame(size) : null));
  const { user, loading: authLoading } = useAuth();

  // The match id in the URL (?m=951 -> room instance "public:<size>#951"). Present on
  // a reload or a shared link: connect straight to that match, skipping the
  // matchmaker. Absent on a fresh join: the base room resolves via the matchmaker and
  // onRoomInstance writes the id into the URL (replace, no history spam). Captured
  // once per join so the RemoteGame roomId prop never changes mid-session; Play
  // again bumps joinKey, which remounts RemoteGame against the base room.
  const [searchParams, setSearchParams] = useSearchParams();
  const [joinKey, setJoinKey] = useState(0);
  const roomId = useMemo(() => {
    if (joinKey > 0) return "public:" + size;
    const m = (searchParams.get("m") || "").trim();
    const ok = /^[A-Za-z0-9_-]{1,32}$/.test(m);
    return ok ? "public:" + size + "#" + m : "public:" + size;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [joinKey]);
  const onRoomInstance = useCallback((instance: string) => {
    const hash = instance.indexOf("#");
    if (hash <= 0) return;
    // Merge with whatever params are already there (debug/tuning knobs survive).
    const next = new URLSearchParams(window.location.search);
    next.set("m", instance.slice(hash + 1));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [session, setSession] = useState<OnlineSession | null>(null);
  // Live box counts (capacity + this cycle's map), for the waiting modal's facts.
  const [counts, setCounts] = useState<LobbyCounts>({});
  useEffect(() => subscribeLobbyCounts((c) => setCounts(c)), []);
  // The lowest launch estimate shown so far (the line never grows on a joiner).
  const etaRef = useRef(0);
  // Spinner until the lobby has other players, so the first frame isn't a lonely
  // "just you" roster; falls through after a few seconds for a genuinely quiet box.
  const [enterTimedOut, setEnterTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setEnterTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, [joinKey]);

  const waiting = !session || session.phase === "connecting" || session.phase === "waiting";

  // 1s tick so the countdown lines advance between server pushes; only while waiting.
  const [, tickNow] = useState(0);
  useEffect(() => {
    if (!waiting) return;
    const t = setInterval(() => tickNow((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [waiting]);

  const leave = useCallback(() => navigate("/"), [navigate]);
  // Play again from the results: drop the finished match's id from the URL and
  // join the same box afresh (the matchmaker picks the next instance).
  const playAgain = useCallback(() => {
    const next = new URLSearchParams(window.location.search);
    next.delete("m");
    setSearchParams(next, { replace: true });
    setSession(null);
    etaRef.current = 0;
    setEnterTimedOut(false);
    setJoinKey((k) => k + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Unknown slug: nothing to join, back to the lobby. The load-test boxes are
  // for admins (performance tests, not game modes): decided once the
  // session has resolved, since `user` is null while it loads and bouncing
  // on that sent admins back to the lobby too.
  const gated = isStressSize(size);
  // The three old boxes are gone (Play Now took the place of Duel and Brawl, the
  // Royale that of Royale Chaos): their old links land on what replaced them.
  // Only the load-test boxes still live on this page.
  const retired = ({ duel: "/play-now", brawl: "/play-now", royale: "/royale" } as Record<string, string>)[(params.mode || "").toLowerCase()];
  const allowed = !!size && !retired && (!gated || isAdmin(user));
  const settled = !gated || !authLoading;
  useEffect(() => {
    if (retired) navigate(retired, { replace: true });
    else if (settled && !allowed) navigate("/", { replace: true });
  }, [settled, allowed, retired, navigate]);
  if (!settled || !allowed || !g) return null;

  const roster = session?.players ?? [];
  const youIds = session?.youIds ?? [];
  const connecting = !session || session.phase === "connecting" || roster.length === 0;
  const entering = connecting || (roster.length <= 1 && !enterTimedOut);
  const cap = counts[g.size]?.capacity ?? g.capacity;
  const secs = session?.startAt ? Math.max(0, Math.ceil((session.startAt - Date.now()) / 1000)) : 0;
  if (session?.etaAt) etaRef.current = etaRef.current ? Math.min(etaRef.current, session.etaAt) : session.etaAt;
  const etaSecs = etaRef.current ? Math.ceil((etaRef.current - Date.now()) / 1000) : secs;
  const soon = secs <= 0 || etaSecs <= 5;
  const estimate = secs <= 0 ? "Starting…"
    : etaSecs <= 5 ? "Any second now"
    : etaSecs > 45 ? "In about a minute"
    : "In about " + Math.max(5, Math.round(etaSecs / 5) * 5) + " seconds";
  const liveMap = (counts[g.size]?.map && descToMapDef(counts[g.size]!.map)) || g.map;
  const shape = liveMap.value.split(" ")[0].toLowerCase(); // circle | square | rectangle | stadium
  const cshape = shape.charAt(0).toUpperCase() + shape.slice(1);

  return (
    // Two boxes, not one. The OUTER is the full-viewport cover and the scroller, so
    // the match-over page scrolls as a whole page with its scrollbar on the browser's
    // edge. The INNER is the centred, width-capped content column (.page-main-game).
    // As one box, the cap (1440px) made the fixed cover itself narrower than a wide
    // screen, and its scrollbar sat in the middle of the window.
    <div style={{ position: "fixed", inset: 0, background: "#070a12", overflowY: waiting ? "visible" : "auto" }}>
      <div className="page-main page-main-game">
        <RemoteGame key={joinKey} roomId={roomId} onSession={setSession} onRoomInstance={onRoomInstance} onLeave={leave} onPlayAgain={playAgain} />
      </div>
      {waiting && (
        <Modal
          className={styles.stageModal}
          title={entering ? "Joining lobby…" : "Looking for players"}
          onClose={leave}
          footer={
            <>
              <button className="app-btn app-btn-ghost" onClick={leave}>Leave</button>
              {/* Force-starting the box before it fills is an admin-only dev tool. */}
              {isAdmin(user) && (
                <button className="app-btn app-btn-primary" onClick={() => session?.startNow()} disabled={connecting}>Start now ▸</button>
              )}
            </>
          }
        >
          <div className={styles.stageHead}>
            <div className={`${styles.stageModeBadge} ${styles["stageShape" + cshape]}`}><i /></div>
            <div>
              <div className={styles.stageModeTitle}>{g.title}</div>
              {/* Match facts: the exact map (value includes the size) and the winning
                  condition - the host's explicit target/rounds when set, otherwise the
                  server's default formula (first to max(10, seats*2)). */}
              <div className={styles.stageModeSub}>
                {arenaLabel(liveMap.value)}
                {session?.config?.maxRounds
                  ? ` · ${session.config.maxRounds} rounds`
                  : ` · First to ${session?.config?.maxScore ?? Math.max(10, (cap || 5) * 2)} points`}
              </div>
            </div>
          </div>
          {entering ? (
            // Entering: a spinner while the lobby fills, so the first frame isn't a
            // lonely "just you" roster.
            <div className={styles.stageEntering}>
              <div className={styles.stageSpinner} aria-hidden />
              <div className={styles.stageEnteringText}>Connecting to lobby…</div>
            </div>
          ) : (
            <>
              <div className={styles.stageTags}>
                {roster.map((p: any) => (
                  <span key={p.id} className={styles.stageTag + (youIds.includes(p.id) ? " " + styles.me : "")}>
                    <i className={styles.stageTagDot} style={{ background: wormColors[p.colorId] }} />
                    {p.name}{youIds.includes(p.id) ? " (you)" : ""}
                  </span>
                ))}
              </div>
              <div className={styles.stageStatusbar}>
                <div>
                  <div className={styles.stageStatusLbl}>Estimated start</div>
                  <div className={styles.stageStatusVal + (soon ? " " + styles.soon : "")}>{estimate}</div>
                  {/* The box's own clock, quietly: the latest the match can start. */}
                  {secs > 0 && <div className={styles.stageStatusSub}>Latest start {fmtClock(secs)}</div>}
                </div>
                <div className={styles.stageStatusCount}>{roster.length}/{cap}</div>
                {/* Same cyan fill the big matchmaking cards show, along the bottom edge. */}
                <i className={styles.stageStatusFill} style={{ width: (cap ? Math.min(100, (roster.length / cap) * 100) : 0) + "%" }} />
              </div>
            </>
          )}
        </Modal>
      )}
    </div>
  );
}
